import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  AppBarProps,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import InstagramIcon from '@mui/icons-material/Instagram';
import logo from '../../assets/full_logo.png';

interface HeaderProps {
  scrollToSection: (ref: React.RefObject<HTMLDivElement | null>) => void;
  contactRef?: React.RefObject<HTMLDivElement | null>;
}

interface ElevationScrollProps {
  children: React.ReactElement<AppBarProps>;
}

function ElevationScroll(props: ElevationScrollProps) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    sx: {
      backgroundColor: trigger ? 'background.paper' : 'transparent',
      color: trigger ? 'text.primary' : 'text.primary',
      transition: '0.3s',
    },
  });
}

const Header: React.FC<HeaderProps> = ({ scrollToSection, contactRef }) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavClick = (
    ref: React.RefObject<HTMLDivElement | null> | undefined,
  ) => {
    if (ref) {
      scrollToSection(ref);
      if (drawerOpen) {
        setDrawerOpen(false);
      }
    }
  };

  const handleNavigate = (path: string) => {
    if (location.pathname !== path) {
      window.scrollTo(0, 0);
      navigate(path);
    }
    if (drawerOpen) {
      setDrawerOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 'auto' }} role="presentation">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="ClassAct Logo"
          sx={{ height: 30 }}
        />
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        <ListItemButton
          onClick={() => handleNavigate('/')}
          selected={location.pathname === '/'}
        >
          <ListItemText primary="About" />
        </ListItemButton>

        <ListItemButton
          onClick={() => handleNavigate('/law-firms')}
          selected={location.pathname === '/law-firms'}
        >
          <ListItemText primary="ClassAct for Law Firms" />
        </ListItemButton>

        <ListItemButton
          onClick={() => handleNavigate('/plaintiffs')}
          selected={location.pathname === '/plaintiffs'}
        >
          <ListItemText primary="ClassAct for Plaintiffs" />
        </ListItemButton>

        {contactRef && (
          <ListItemButton onClick={() => handleNavClick(contactRef)}>
            <ListItemText primary="Contact" />
          </ListItemButton>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <IconButton
          color="primary"
          href="https://www.instagram.com/classactapp"
          target="_blank"
        >
          <InstagramIcon />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <ElevationScroll>
        <AppBar
          position="fixed"
          sx={{
            boxShadow: 'none',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: '48px !important', // Override MUI's default
              height: 48,
              px: 2,
              py: 0,
              '&.MuiToolbar-root': {
                minHeight: '48px',
              },
              justifyContent: 'space-between',
            }}
          >
            <Box
              onClick={() => handleNavigate('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexBasis: '25%',
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="ClassAct Logo"
                sx={{
                  height: 24,
                  mr: 'auto',
                  cursor: 'pointer',
                }}
              />
            </Box>

            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexBasis: '50%',
                  alignItems: 'center',
                }}
              >
                <Button
                  color="inherit"
                  onClick={() => handleNavigate('/')}
                  sx={{
                    mx: 1,
                    fontWeight: 400,
                    color:
                      location.pathname === '/' ? 'primary.main' : 'inherit',
                    py: 0.5,
                    fontSize: '0.9rem',
                  }}
                >
                  About
                </Button>
                <Button
                  color="inherit"
                  onClick={() => handleNavigate('/law-firms')}
                  sx={{
                    mx: 1,
                    fontWeight: 400,
                    color:
                      location.pathname === '/law-firms'
                        ? 'primary.main'
                        : 'inherit',
                    py: 0.5,
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ClassAct For Law Firms
                </Button>
                <Button
                  color="inherit"
                  onClick={() => handleNavigate('/plaintiffs')}
                  sx={{
                    mx: 1,
                    fontWeight: 400,
                    color:
                      location.pathname === '/plaintiffs'
                        ? 'primary.main'
                        : 'inherit',
                    py: 0.5,
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ClassAct For Plaintiffs
                </Button>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexBasis: '25%',
              }}
            >
              {!isMobile && contactRef && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleNavClick(contactRef)}
                  sx={{
                    ml: 'auto',
                    borderRadius: '20px',
                    px: 2,
                    py: 0.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                  }}
                >
                  Get Started
                </Button>
              )}
            </Box>

            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ ml: 'auto' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      </ElevationScroll>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '300px',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
